/* eslint-disable @typescript-eslint/no-use-before-define --
 * The reducer and action handlers are mutually recursive (e.g.
 * commitPending calls continueFromConfirmation which calls enterSpoke,
 * and enterSpoke calls resolveEntry). Topologically sorting them by
 * call order would be pointless churn; function declarations hoist, so
 * forward references are safe.
 */
/**
 * Suite Builder engine — a reducer over walk state + suite state.
 *
 * Two state blobs:
 *   - SuiteState: the durable record of what the shopper has picked. Just
 *     a list of committed contributions. Joining kits are derived, never
 *     stored.
 *   - WalkState: the transient state of the active walk (current screen,
 *     pending contribution, navigation history, sequence frames). null
 *     when the shopper is on the landing page.
 *
 * Rules live at the bottom: deriveAutoAdds, evaluateDiscount.
 * Both read Suite only — never WalkState, never the screen graph.
 */
import type { ImageChooserBlock, Money } from "@reactivated";

import { fallbackTileImage } from "../../../utils/fallbackTileImage";
import { MergedProduct, withAmount } from "../ProductContext/merge";
import { nodes } from "./nodes";
import {
    autoAddRules,
    discountRules,
    getOptionId,
    hub,
    isAccessory,
    skuCatalog,
} from "./schema";
import type {
    ChainEntry,
    DiscountRole,
    EngineImage,
    Option,
    PickNode,
    SKU,
    SpokeId,
    StructuralTag,
} from "./schema";

// ---------- Types ----------

export type Contribution = {
    id: string;
    spoke: SpokeId;
    mainSku: SKU;
    accessorySkus: SKU[];
    committedAt: number;
};

export type SuiteState = {
    contributions: Contribution[];
};

/**
 * Sequence frame: when a `sequence` node is entered, we push a frame
 * recording which chain we're walking and where we are in it. When a
 * sub-flow commits, we advance `index`; when `index` runs off the end,
 * we pop the frame.
 */
type SequenceFrame = {
    chainNodeId: string;
    index: number;
};

/**
 * Walk history entry — one per node visited, so Back can unwind cleanly.
 * We track the selected optionIds so "pick any" accessory choices can be
 * reconstructed when the shopper lands back on the screen, and we track
 * which SKUs the node contributed so Back can roll them back out of
 * `pending`.
 */
type HistoryEntry = {
    nodeId: string;
    selectedOptionIds: string[]; // for pick:any, the checkbox state
    emittedSkus: SKU[]; // SKUs this step added to pending (addsSku + option skus)
    /**
     * The walk's `sequenceStack` at the moment this entry was pushed.
     * A history entry represents the walk's state when that screen was
     * active; `sequenceStack` is part of that state, alongside
     * `currentNodeId`, the option selections, and the emitted SKUs.
     * `goBack` restores all of these together so the walk lands back
     * in the same state it was in.
     */
    sequenceStack: SequenceFrame[];
};

type WalkState = {
    spoke: SpokeId;
    entry: "initial" | "populated";
    /**
     * Which Populated Landing surface the shopper entered from. Only set
     * when `entry === "populated"`. Drives the first-screen Back rule:
     * `"hub"` Backs to the hub; `"placeholder"` hides the Back CTA
     * (Back to Populated Landing would duplicate "Back to My Suite").
     */
    populatedOrigin?: "hub" | "placeholder";
    currentNodeId: string;
    history: HistoryEntry[];
    pending: {
        mainSku: SKU | null;
        accessorySkus: SKU[];
    };
    sequenceStack: SequenceFrame[];
    /** Set while the shopper is on a confirmation screen for the just-committed sub-flow. */
    confirmingContributionId: string | null;
    /**
     * Spokes the shopper has exited entirely during this initial walk.
     * Set when (a) a terminal commit lands the shopper on a sub-flow
     * confirmation that ends the spoke (the last chain entry, or a
     * single-sub-flow spoke), or (b) a cross-spoke advance fires (Skip
     * past a non-chain or chain-final screen, or Continue from a
     * terminal confirmation). Not set mid-chain. Drives the progress-
     * bar checkmarks. Lives on the walk because it's session-only and
     * captured by snapshots so Back across boundaries naturally
     * restores the prior set.
     */
    walkedPast: SpokeId[];
    /**
     * Pre-cross walk snapshots for Back across boundaries. Pushed when
     * the engine resets the walk's history (cross-spoke advance, chain
     * advance), so `goBack`'s close-walk branch can restore the prior
     * screen instead of nulling the walk. Skip + Back returns to the
     * spoke/chain entry the shopper skipped from; Commit + Continue +
     * Back lands on the prior confirmation (with the contribution
     * still committed) — a second Back falls into the existing
     * Back-from-confirmation path in `goBack`, which discards the
     * commit, keeping the canonical "undo a commit" mechanic in one
     * place.
     */
    backStack: WalkSnapshot[];
};

type WalkSnapshot = Omit<WalkState, "backStack">;

function snapshotWalk(walk: WalkState): WalkSnapshot {
    const { backStack: _, ...snap } = walk;
    return snap;
}

/**
 * Top-level UI surface. Three discrete modes the engine can be in:
 *   - `"landing"` — Initial or Populated Landing (which one is determined
 *     by `suite.contributions.length`, not by this field).
 *   - `"hub"` — the "Choose a category" picker reached from Populated
 *     Landing's "Add an Appliance" button. Carries no state of its own.
 *   - `"walk"` — a walk is in flight; `walk` is non-null.
 *
 * The hub is modeled as a top-level mode rather than a kind of walk
 * because it shares none of WalkState's bookkeeping (no history, no
 * pending contribution, no sequence stack, no Back).
 */
type View = "landing" | "hub" | "walk";

/**
 * Discriminated union encoding the `view ↔ walk` invariant at the type
 * level: `view === "walk"` is the only variant carrying a non-null
 * `walk`. Constructing a `{ view: "walk", walk: null }` or
 * `{ view: "landing", walk: <WalkState> }` is a compile error rather
 * than a silent runtime desync.
 */
export type AppState =
    | { view: "landing"; suite: SuiteState; walk: null }
    | { view: "hub"; suite: SuiteState; walk: null }
    | { view: "walk"; suite: SuiteState; walk: WalkState };

export type WalkAppState = Extract<AppState, { view: "walk" }>;

// ---------- Initial state ----------

export const emptyState = (): AppState => ({
    view: "landing",
    suite: { contributions: [] },
    walk: null,
});

// ---------- Helpers ----------

function uid(): string {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getPickNode(id: string): PickNode {
    const n = nodes[id];
    if (!n) throw new Error(`Unknown node: ${id}`);
    if (n.type !== "pick") throw new Error(`Expected pick node: ${id}`);
    return n;
}

/** Find an option on a pick node by its canonical identity. */
function findOption(node: PickNode, id: string): Option | undefined {
    return node.options.find((o) => getOptionId(o) === id);
}

/**
 * Innermost-out search for the next unconsumed entry in the sequence
 * stack. Returns null when every frame is fully walked. Throws if a
 * frame's `chainNodeId` doesn't resolve to a sequence node.
 */
function findRemainingChainEntry(
    stack: SequenceFrame[],
): { chainNodeId: string; nextIndex: number; nextEntry: ChainEntry } | null {
    for (let i = stack.length - 1; i >= 0; i--) {
        const frame = stack[i];
        const seq = nodes[frame.chainNodeId];
        if (!seq || seq.type !== "sequence") {
            throw new Error(`sequence stack corruption: ${frame.chainNodeId}`);
        }
        const nextIndex = frame.index + 1;
        if (nextIndex < seq.chain.length) {
            return {
                chainNodeId: frame.chainNodeId,
                nextIndex,
                nextEntry: seq.chain[nextIndex],
            };
        }
    }
    return null;
}

/**
 * Merge one SKU into the pending contribution. A main-product SKU sets
 * `pending.mainSku`; accessories append to `accessorySkus`.
 */
function applyEmit(
    pending: WalkState["pending"],
    sku: SKU,
): WalkState["pending"] {
    if (isAccessory(sku)) {
        if (pending.accessorySkus.includes(sku)) return pending;
        return { ...pending, accessorySkus: [...pending.accessorySkus, sku] };
    }
    return { ...pending, mainSku: sku };
}

// ---------- Action handlers (defined before the reducer that dispatches to them) ----------

/**
 * Derive a populated walk's origin from the prior view. Entering from
 * the hub makes the walk hub-style; entering from anywhere else (in
 * practice: Populated Landing's per-spoke placeholder) makes it
 * placeholder-style. The distinction drives the Back rule on the
 * walk's first screen — see `isBackVisible`. Populated walks don't
 * cross-spoke advance, so the only two surfaces they can be entered
 * from are the hub and a placeholder; initial walks have no origin.
 */
function inferPopulatedOrigin(
    priorView: AppState["view"],
    entry: "initial" | "populated",
): "hub" | "placeholder" | undefined {
    if (entry !== "populated") return undefined;
    return priorView === "hub" ? "hub" : "placeholder";
}

function enterSpoke(
    state: AppState,
    spokeId: SpokeId,
    entry: "initial" | "populated",
): AppState {
    const spoke = hub.spokes.find((s) => s.id === spokeId);
    if (!spoke) throw new Error(`Unknown spoke: ${spokeId}`);

    const rootId =
        entry === "populated" && spoke.rootNodePopulated
            ? spoke.rootNodePopulated
            : spoke.rootNode;

    // Resolve: the "root" may itself be a sequence node. If so, unwrap.
    const { nodeId, sequenceStack } = resolveEntry(rootId, []);

    // Apply the landing node's addsSku (rare at a spoke root, but the
    // mechanic is uniform).
    const node = getPickNode(nodeId);
    let pending: WalkState["pending"] = { mainSku: null, accessorySkus: [] };
    const emitted: SKU[] = [];
    if (node.addsSku) {
        pending = applyEmit(pending, node.addsSku);
        emitted.push(node.addsSku);
    }

    // If we're entering this spoke from an existing walk (cross-spoke
    // advance via SKIP or CONTINUE), push the prior walk onto backStack
    // so Back can restore it. Fresh entries (from the Initial / Populated Landing)
    // start with an empty backStack — Back from the first screen of
    // those returns to the landing page, which is the right behaviour.
    const backStack: WalkSnapshot[] = state.walk
        ? [...state.walk.backStack, snapshotWalk(state.walk)]
        : [];

    // Cross-spoke advance also marks the prior spoke walked-past for the
    // progress bar. Idempotent insert because commitPending may already
    // have flagged it on a terminal-commit confirmation. Fresh entries
    // start with an empty walkedPast.
    let walkedPast: SpokeId[] = state.walk?.walkedPast ?? [];
    if (state.walk && !walkedPast.includes(state.walk.spoke)) {
        walkedPast = [...walkedPast, state.walk.spoke];
    }

    const populatedOrigin = inferPopulatedOrigin(state.view, entry);

    return {
        ...state,
        view: "walk",
        walk: {
            spoke: spokeId,
            entry,
            populatedOrigin,
            currentNodeId: nodeId,
            history: [
                {
                    nodeId,
                    selectedOptionIds: [],
                    emittedSkus: emitted,
                    sequenceStack,
                },
            ],
            pending,
            sequenceStack,
            confirmingContributionId: null,
            walkedPast,
            backStack,
        },
    };
}

/**
 * Cycle guard for `resolveEntry`: how many nested sequence unwraps to
 * do before assuming the schema has a `then:` cycle. Real schema depth
 * is 1 (no chain points to another chain); the bound is generous, not
 * load-bearing — if it ever fires, the answer is "find the cycle,"
 * not "raise the limit."
 */
const MAX_SEQUENCE_NEST_DEPTH = 10;

/**
 * Given a target node id, walk through any `sequence` wrappers until we
 * land on a `pick` node. Returns the resolved pick node id and the
 * sequence stack built up along the way.
 */
function resolveEntry(
    targetId: string,
    incomingStack: SequenceFrame[],
): { nodeId: string; sequenceStack: SequenceFrame[] } {
    let current = targetId;
    const stack = [...incomingStack];
    for (let i = 0; i < MAX_SEQUENCE_NEST_DEPTH; i++) {
        const node = nodes[current];
        if (!node) throw new Error(`Unknown node: ${current}`);
        if (node.type === "pick")
            return { nodeId: current, sequenceStack: stack };
        stack.push({ chainNodeId: current, index: 0 });
        const first = node.chain[0];
        current = typeof first === "string" ? first : first.node;
    }
    throw new Error(
        `resolveEntry: more than ${MAX_SEQUENCE_NEST_DEPTH} nested ` +
            `sequences from ${targetId} — likely a cycle in the schema.`,
    );
}

function applyPickOne(state: WalkAppState, optionId: string): AppState {
    const walk = state.walk;
    const node = getPickNode(walk.currentNodeId);
    if (node.pick !== "one") throw new Error("PICK_ONE on non-one node");

    const option = findOption(node, optionId);
    if (!option)
        throw new Error(`Unknown option ${optionId} on ${node.prompt}`);

    // Emit option-level SKU (if any).
    let pending = walk.pending;
    const emittedNow: SKU[] = [];
    if (option.sku) {
        pending = applyEmit(pending, option.sku);
        emittedNow.push(option.sku);
    }

    // Record the selection on the current history entry (for Back + display).
    const updatedHistory = walk.history.map((h, i) =>
        i === walk.history.length - 1
            ? {
                  ...h,
                  selectedOptionIds: [optionId],
                  emittedSkus: [...h.emittedSkus, ...emittedNow],
              }
            : h,
    );

    // Figure out next step.
    const nextId = option.then ?? node.then;
    if (!nextId) {
        // Terminal: this tile was the last decision. Commit the contribution.
        return commitPending({
            ...state,
            walk: { ...walk, pending, history: updatedHistory },
        });
    }

    return advanceTo(
        { ...state, walk: { ...walk, pending, history: updatedHistory } },
        nextId,
    );
}

function toggleAny(state: WalkAppState, optionId: string): WalkAppState {
    const walk = state.walk;
    const node = getPickNode(walk.currentNodeId);
    if (node.pick !== "any") throw new Error("TOGGLE_ANY on non-any node");
    const option = findOption(node, optionId);
    if (!option?.sku) {
        throw new Error(`Unknown option ${optionId} on ${node.prompt}`);
    }

    const last = walk.history[walk.history.length - 1];
    const isSelected = last.selectedOptionIds.includes(optionId);
    const newSelected = isSelected
        ? last.selectedOptionIds.filter((id) => id !== optionId)
        : [...last.selectedOptionIds, optionId];

    // Rebuild pending for this step: strip this node's prior option
    // emissions, then re-apply the node's `addsSku` and the currently-
    // selected options.
    let pending: WalkState["pending"] = {
        mainSku: walk.pending.mainSku,
        accessorySkus: walk.pending.accessorySkus.filter(
            (sku) => !last.emittedSkus.includes(sku),
        ),
    };
    const emitted: SKU[] = [];
    if (node.addsSku) {
        pending = applyEmit(pending, node.addsSku);
        emitted.push(node.addsSku);
    }
    for (const id of newSelected) {
        const opt = findOption(node, id);
        if (opt?.sku) {
            pending = applyEmit(pending, opt.sku);
            emitted.push(opt.sku);
        }
    }

    return {
        ...state,
        walk: {
            ...walk,
            pending,
            history: walk.history.map((h, i) =>
                i === walk.history.length - 1
                    ? {
                          ...h,
                          selectedOptionIds: newSelected,
                          emittedSkus: emitted,
                      }
                    : h,
            ),
        },
    };
}

function submitAny(state: WalkAppState): AppState {
    const walk = state.walk;
    const node = getPickNode(walk.currentNodeId);
    if (node.pick !== "any") throw new Error("SUBMIT_ANY on non-any node");
    // "any" nodes don't route per-option; they always use node-level then.
    if (!node.then) return commitPending(state);
    return advanceTo(state, node.then);
}

/**
 * Composite of TOGGLE_ANY + SUBMIT_ANY for the design's "tap-to-pick-
 * and-advance" semantic on single-option pick:any screens (back guards,
 * duct covers, trim kit). The renderer dispatches this when there is
 * exactly one option, so one tile click both selects the accessory and
 * commits the pending contribution. Idempotent if the option is already
 * selected (avoids double-toggling it back off).
 */
function pickAnyAndSubmit(state: WalkAppState, optionId: string): AppState {
    const walk = state.walk;
    const node = getPickNode(walk.currentNodeId);
    if (node.pick !== "any")
        throw new Error("PICK_ANY_AND_SUBMIT on non-any node");
    // Validate up front — the already-selected branch below skips toggleAny.
    const option = findOption(node, optionId);
    if (!option?.sku) {
        throw new Error(`Unknown option ${optionId} on ${node.prompt}`);
    }
    const lastEntry = walk.history[walk.history.length - 1];
    const stateWithSelection: WalkAppState =
        lastEntry.selectedOptionIds.includes(optionId)
            ? state
            : toggleAny(state, optionId);
    return submitAny(stateWithSelection);
}

function advanceTo(state: WalkAppState, nextId: string): WalkAppState {
    const walk = state.walk;
    const resolved = resolveEntry(nextId, walk.sequenceStack);
    const next = getPickNode(resolved.nodeId);

    // Apply the new node's addsSku.
    let pending = walk.pending;
    const emitted: SKU[] = [];
    if (next.addsSku) {
        pending = applyEmit(pending, next.addsSku);
        emitted.push(next.addsSku);
    }

    return {
        ...state,
        walk: {
            ...walk,
            currentNodeId: resolved.nodeId,
            history: [
                ...walk.history,
                {
                    nodeId: resolved.nodeId,
                    selectedOptionIds: [],
                    emittedSkus: emitted,
                    sequenceStack: resolved.sequenceStack,
                },
            ],
            pending,
            sequenceStack: resolved.sequenceStack,
        },
    };
}

/**
 * Commit the pending contribution to the suite and enter the
 * confirmation view. From there, CONTINUE_FROM_CONFIRMATION either
 * advances to the next sequence entry, or exits the walk.
 */
function commitPending(state: WalkAppState): AppState {
    const walk = state.walk;
    if (!walk.pending.mainSku) {
        // Unreachable for any schema that passes validateNodes(); the
        // throw catches engine bugs that bypass the schema.
        throw new Error(
            `commitPending reached with null mainSku at ${walk.currentNodeId}`,
        );
    }
    const contribution: Contribution = {
        id: uid(),
        spoke: walk.spoke,
        mainSku: walk.pending.mainSku,
        accessorySkus: walk.pending.accessorySkus,
        committedAt: Date.now(),
    };

    // If this commit terminates the spoke (no remaining chain entries to
    // advance to), the confirmation we're about to render is the spoke's
    // terminal one — flag the spoke walked-past so the progress bar
    // checks it before the shopper presses Continue. Only meaningful on
    // initial walks; populated walks never render the progress bar.
    const isTerminal = findRemainingChainEntry(walk.sequenceStack) === null;
    const walkedPast =
        walk.entry === "initial" &&
        isTerminal &&
        !walk.walkedPast.includes(walk.spoke)
            ? [...walk.walkedPast, walk.spoke]
            : walk.walkedPast;

    return {
        ...state,
        suite: { contributions: [...state.suite.contributions, contribution] },
        walk: {
            ...walk,
            confirmingContributionId: contribution.id,
            walkedPast,
        },
    };
}

/**
 * Shared "advance past the current sub-flow" logic. Used both when the
 * shopper confirms a commit (CONTINUE_FROM_CONFIRMATION) and when they
 * Skip the current sub-flow without committing (SKIP_SPOKE). The two
 * UI affordances differ in *what came before* (commit vs. abandon) but
 * the forward-motion is identical: advance within the active sequence
 * if there's another entry, otherwise fall through to the next spoke
 * (initial walk) or end the walk (populated entry / final spoke).
 */
function advancePastCurrentSubflow(state: WalkAppState): AppState {
    const walk = state.walk;
    const stack = [...walk.sequenceStack];
    while (stack.length) {
        const top = stack[stack.length - 1];
        const seq = nodes[top.chainNodeId];
        if (seq.type !== "sequence")
            throw new Error("sequence stack corruption");
        const nextIdx = top.index + 1;
        if (nextIdx < seq.chain.length) {
            const newStack = [
                ...stack.slice(0, -1),
                { ...top, index: nextIdx },
            ];
            const nextEntry = seq.chain[nextIdx];
            const nextTarget =
                typeof nextEntry === "string" ? nextEntry : nextEntry.node;
            const resolved = resolveEntry(nextTarget, newStack);
            const nextNode = getPickNode(resolved.nodeId);
            let pending: WalkState["pending"] = {
                mainSku: null,
                accessorySkus: [],
            };
            const emitted: SKU[] = [];
            if (nextNode.addsSku) {
                pending = applyEmit(pending, nextNode.addsSku);
                emitted.push(nextNode.addsSku);
            }
            // Crossing a chain boundary — snapshot pre-cross walk so
            // Back can restore it.
            return {
                ...state,
                walk: {
                    ...walk,
                    currentNodeId: resolved.nodeId,
                    history: [
                        {
                            nodeId: resolved.nodeId,
                            selectedOptionIds: [],
                            emittedSkus: emitted,
                            sequenceStack: resolved.sequenceStack,
                        },
                    ],
                    pending,
                    sequenceStack: resolved.sequenceStack,
                    confirmingContributionId: null,
                    backStack: [...walk.backStack, snapshotWalk(walk)],
                },
            };
        }
        stack.pop();
    }

    if (walk.entry === "initial") {
        const order = hub.spokes.map((s) => s.id);
        const idx = order.indexOf(walk.spoke);
        const nextSpoke = order[idx + 1];
        if (nextSpoke) return enterSpoke(state, nextSpoke, "initial");
    }
    // Walk over (final-spoke initial walk completed, or populated walk
    // ended). Return to the landing surface.
    return { ...state, view: "landing", walk: null };
}

function continueFromConfirmation(state: WalkAppState): AppState {
    return advancePastCurrentSubflow(state);
}

function skipSpoke(state: WalkAppState): AppState {
    return advancePastCurrentSubflow(state);
}

function goBack(state: WalkAppState): AppState {
    const walk = state.walk;
    if (walk.confirmingContributionId) {
        // Back from confirmation: discard the just-committed contribution
        // and return the last screen to its as-entered state so the user
        // can make a different choice without the previous pick lingering.
        const discardId = walk.confirmingContributionId;
        const lastEntry = walk.history[walk.history.length - 1];
        const lastNode = nodes[lastEntry.nodeId];
        // As-entered emissions = just the node's addsSku (if any). Everything
        // else in emittedSkus came from the user's option selections, which
        // we're rolling back.
        const resetEmitted: SKU[] =
            lastNode.type === "pick" && lastNode.addsSku
                ? [lastNode.addsSku]
                : [];
        const resetHistory = walk.history.map((h, i) =>
            i === walk.history.length - 1
                ? { ...h, selectedOptionIds: [], emittedSkus: resetEmitted }
                : h,
        );
        // If the just-undone commit was the one that flagged this spoke
        // walked-past (terminal commit on a single-sub-flow spoke or last
        // chain entry), revert that flag too. Without this, the progress
        // bar would still show the spoke as Completed even though the
        // shopper is back on the picker with no commit. Cross-spoke advances
        // (the other path that adds to walkedPast) are reverted via the
        // back-stack snapshot mechanism instead — see the close-walk branch
        // below.
        const revertedWalkedPast = walk.walkedPast.filter(
            (s) => s !== walk.spoke,
        );
        return {
            ...state,
            suite: {
                contributions: state.suite.contributions.filter(
                    (c) => c.id !== discardId,
                ),
            },
            walk: {
                ...walk,
                confirmingContributionId: null,
                history: resetHistory,
                pending: reconstructPendingFromHistory(resetHistory),
                walkedPast: revertedWalkedPast,
            },
        };
    }

    if (walk.history.length <= 1) {
        // History exhausted within this sub-flow. If there's a snapshot
        // from a prior boundary cross, restore it (un-doing the most
        // recent Skip / Continue). Otherwise this really is the first
        // screen of the walk — exit to whichever surface the shopper
        // came from.
        if (walk.backStack.length > 0) {
            const snap = walk.backStack[walk.backStack.length - 1];
            return {
                ...state,
                walk: { ...snap, backStack: walk.backStack.slice(0, -1) },
            };
        }
        // Hub-entered populated walks Back to the hub. Everything else
        // (initial walks, placeholder-entered populated walks) Backs to
        // the landing page. The placeholder-entered case is unreachable
        // through normal UI — the renderer hides Back on that screen
        // since it would duplicate "Back to My Suite" — but defaulting
        // to "landing" keeps the engine sensible if the dispatch fires.
        const nextView: View =
            walk.populatedOrigin === "hub" ? "hub" : "landing";
        return { ...state, view: nextView, walk: null };
    }
    // Pop the last entry, and reset the destination (the new last entry) to
    // its as-entered state so the user can re-pick without the prior choice
    // lingering in selectedOptionIds or double-emitting into pending.
    const trimmed = walk.history.slice(0, -1);
    const prevEntry = trimmed[trimmed.length - 1];
    const prevNode = nodes[prevEntry.nodeId];
    const resetEmitted: SKU[] =
        prevNode.type === "pick" && prevNode.addsSku ? [prevNode.addsSku] : [];
    const resetHistory = [
        ...trimmed.slice(0, -1),
        { ...prevEntry, selectedOptionIds: [], emittedSkus: resetEmitted },
    ];
    return {
        ...state,
        walk: {
            ...walk,
            currentNodeId: prevEntry.nodeId,
            history: resetHistory,
            pending: reconstructPendingFromHistory(resetHistory),
            sequenceStack: prevEntry.sequenceStack,
        },
    };
}

function reconstructPendingFromHistory(
    history: HistoryEntry[],
): WalkState["pending"] {
    let pending: WalkState["pending"] = { mainSku: null, accessorySkus: [] };
    for (const entry of history) {
        for (const sku of entry.emittedSkus) {
            pending = applyEmit(pending, sku);
        }
    }
    return pending;
}

// ---------- Actions + reducer ----------

export type Action =
    | { type: "START_INITIAL_WALK" } // from Initial Landing
    | { type: "OPEN_HUB" } // from Populated Landing
    | { type: "ENTER_SPOKE"; spoke: SpokeId; entry: "initial" | "populated" }
    | { type: "PICK_ONE"; optionId: string }
    | { type: "TOGGLE_ANY"; optionId: string }
    | { type: "SUBMIT_ANY" }
    | { type: "PICK_ANY_AND_SUBMIT"; optionId: string }
    | { type: "BACK" }
    | { type: "CONTINUE_FROM_CONFIRMATION" }
    | { type: "SKIP_SPOKE" }
    | { type: "CLOSE_BUILDER" }
    | { type: "REMOVE_CONTRIBUTION"; contributionId: string }
    | { type: "HYDRATE_SUITE"; suite: SuiteState };

export function reduce(state: AppState, action: Action): AppState {
    switch (action.type) {
        case "START_INITIAL_WALK":
            return enterSpoke(state, hub.spokes[0].id, "initial");

        case "ENTER_SPOKE":
            return enterSpoke(state, action.spoke, action.entry);

        case "PICK_ONE":
            return state.walk ? applyPickOne(state, action.optionId) : state;

        case "TOGGLE_ANY":
            return state.walk ? toggleAny(state, action.optionId) : state;

        case "SUBMIT_ANY":
            return state.walk ? submitAny(state) : state;

        case "PICK_ANY_AND_SUBMIT":
            return state.walk
                ? pickAnyAndSubmit(state, action.optionId)
                : state;

        case "BACK":
            return state.walk ? goBack(state) : state;

        case "CONTINUE_FROM_CONFIRMATION":
            return state.walk ? continueFromConfirmation(state) : state;

        case "SKIP_SPOKE":
            return state.walk ? skipSpoke(state) : state;

        case "CLOSE_BUILDER":
            // Universal close — works from the hub (walk is already
            // null), from a walk in flight (clears walk and resets view),
            // and harmlessly from landing (no-op shape change).
            return { ...state, view: "landing", walk: null };

        case "REMOVE_CONTRIBUTION":
            // Removal is a post-walk affordance per spec. The UI hides the
            // Remove button during walks (see ContributionCard); throwing
            // here surfaces any future regression where it leaks back in.
            if (state.view === "walk") {
                throw new Error("REMOVE_CONTRIBUTION dispatched during a walk");
            }
            return {
                ...state,
                suite: {
                    contributions: state.suite.contributions.filter(
                        (c) => c.id !== action.contributionId,
                    ),
                },
            };

        case "OPEN_HUB":
            // Defensive: also clear `walk`. The UI only dispatches
            // OPEN_HUB from Populated Landing where walk is already null,
            // but enforcing the invariant (view === "walk" iff walk !==
            // null) here means a stray dispatch can't desync the two.
            return { ...state, view: "hub", walk: null };

        case "HYDRATE_SUITE":
            // Cross-tab broadcast: replace the durable suite without
            // touching view/walk. The walk's `pending` references SKUs
            // (resolved against the catalog at render time, not the
            // suite), so it survives. A confirmation screen referencing
            // a contribution id that's no longer present is handled by
            // `getConfirmation` returning null. Cross-tab edits during
            // an in-flight walk are best-effort: `walk.history` /
            // `walk.backStack` snapshots may reference contribution ids
            // that no longer exist, and Back through them surfaces the
            // same null-confirmation case until the walk closes.
            return { ...state, suite: action.suite };
    }
}

// ---------- Navigation targets (Skip / Continue) ----------

/**
 * Structural description of where a navigation action (Skip or Continue)
 * leads. The engine returns the shape and the destination's name; the
 * UI-side resolvers compose the full button copy ("Skip to X", "Next:
 * X"). Both Skip and Continue share this discriminator since they
 * answer the same question ("what's next?") even though their button
 * labels differ.
 *
 * `targetLabel` is the destination's display name as authored in the
 * schema (e.g. "Wall Oven", "2nd Column", "Refrigeration"). Schema-side
 * label fields are wrapped with `t` from ttag at definition time, so
 * the string is already translated by the time it reaches the engine.
 *
 * - `next-chain-entry`: another sub-flow remains in the active
 *   sequence. May be `undefined` for bare-string chain entries with no
 *   label.
 * - `next-spoke`: chain done (or no chain), another canonical spoke
 *   remains.
 * - `complete`: walk terminates. UI labels this differently for Skip
 *   ("Skip and Complete") vs. Continue ("Complete My Suite").
 */
export type NavigationTarget =
    | { kind: "next-chain-entry"; targetLabel: string | undefined }
    | { kind: "next-spoke"; targetLabel: string }
    | { kind: "complete" };

/**
 * Where a Skip from the current screen leads. `null` means no Skip —
 * the button should be hidden / labeled "Cancel" and dispatching
 * SKIP_SPOKE will abandon the walk. This is the populated-entry case
 * (subsequent adds from Populated Landing).
 */
export function getSkipTarget(walk: WalkState): NavigationTarget | null {
    if (walk.entry === "populated") return null;
    return navigationTargetFrom(walk);
}

/**
 * Where Continue from a confirmation screen lands. Always returns a
 * target — populated walks fall straight to `complete` (returning to
 * Populated Landing) regardless of remaining canonical spokes.
 */
export function getContinueTarget(walk: WalkState): NavigationTarget {
    return navigationTargetFrom(walk);
}

/**
 * Whether to render the Back button on the current screen. Hidden on
 * the first screen of a populated walk entered via the per-spoke
 * placeholder ("+ Add an Appliance" on Populated Landing): in that
 * case Back would only return to Populated Landing, which the
 * footer's "Back to My Suite" CTA already does — so two CTAs would
 * point at the same destination. Every other case keeps Back
 * (initial walks unwind history; hub-entered populated walks Back to
 * the hub on the first screen; mid-walk screens unwind within the
 * sub-flow).
 */
export function isBackVisible(walk: WalkState): boolean {
    const onFirstScreen =
        walk.history.length <= 1 && walk.backStack.length === 0;
    const placeholderEntry =
        walk.entry === "populated" && walk.populatedOrigin === "placeholder";
    return !(placeholderEntry && onFirstScreen);
}

function navigationTargetFrom(walk: WalkState): NavigationTarget {
    const remaining = findRemainingChainEntry(walk.sequenceStack);
    if (remaining) {
        const targetLabel =
            typeof remaining.nextEntry === "string"
                ? undefined
                : remaining.nextEntry.label;
        return { kind: "next-chain-entry", targetLabel };
    }

    // All chains exhausted. On initial walks, advance to the next
    // canonical spoke if any remain. Populated walks (and final-spoke
    // initial walks) fall through to `complete`.
    if (walk.entry === "initial") {
        const order = hub.spokes.map((s) => s.id);
        const idx = order.indexOf(walk.spoke);
        const nextSpokeId = order[idx + 1];
        if (nextSpokeId) {
            const sp = hub.spokes.find((s) => s.id === nextSpokeId);
            if (!sp) throw new Error(`Unknown spoke: ${nextSpokeId}`);
            return { kind: "next-spoke", targetLabel: sp.label };
        }
    }
    return { kind: "complete" };
}

// ---------- Screen / Confirmation view models ----------

/**
 * View-model selectors. The UI consumes these instead of reaching into
 * `walk.history`, `walk.sequenceStack`, or `skuCatalog` directly. That
 * keeps the Screen/Confirmation components a thin renderer layer and
 * lets the engine evolve internal shapes (e.g. `walked_past` tracking,
 * persistence metadata) without rippling through the UI.
 *
 * SKU-derived fields (label, price) are still resolved against the
 * static `skuCatalog` here; when the API integration replaces that with
 * a hook-provided product context, those reads move to the consumer's
 * context-lookup at render time. The selector's output shape stays the
 * same.
 */

export type ScreenOption = {
    id: string;
    label: string;
    description?: string;
    /**
     * Tile image: authored override (lightweight `EngineImage` descriptor
     * pointing at a file under `static/suite-builder/`), cascade-selected
     * SKU's catalog `ImageChooserBlock`, or the bundled fallback. See
     * `resolveImageSource` for the cascade rules. The render layer
     * (`OptionTile`) discriminates on shape and fills in `STATIC_URL` for
     * the descriptor case.
     */
    image: ImageChooserBlock | EngineImage;
    imageVariant?: "full";
    sku?: SKU;
    /**
     * Display price for the tile. Three cases:
     *   - Direct-SKU options: the catalog price of the option's SKU.
     *   - Grouping options (no SKU, has `then`): the minimum price
     *     across non-accessory products reachable from this tile.
     *   - Suppressed by `PickNode.suppressPrices`: undefined.
     * The renderer formats the value differently depending on
     * `isAccessory` (`+ $X`) and presence of `sku` (exact vs.
     * "Starting at $X").
     */
    price?: Money;
    /**
     * True when the option's SKU is an accessory (the only discount
     * role is `accessory_eligible`). Drives the `+ $X` price prefix on
     * accessory tiles. False for direct main-product SKUs and for
     * grouping options without a SKU.
     */
    isAccessory: boolean;
    /**
     * Enables multi-select state. Intended for pick:any tiles only, and
     * will only be seen in a group of pick:any tiles.
     */
    isSelected?: boolean;
    /**
     * The single main product this tile identifies — its own SKU, or, for a
     * routing tile, a lone product fixed downstream via `addsSku` with no
     * product-choice ahead — when that product has a ProductPage to link to.
     * Drives the "See More Details" affordance. Undefined for accessory tiles,
     * tiles leading to a product choice or to several products, products
     * without a page, or when a sibling tile leads to the same product (see
     * `getCurrentScreen`).
     */
    detailsSku?: SKU;
};

type ScreenViewModel = {
    spoke: SpokeId;
    entry: "initial" | "populated";
    currentNodeId: string;
    prompt: string;
    /** Either the node's own subcopy or the active sequence frame's subcopy. */
    subcopy?: string;
    pickMode: "one" | "any";
    options: ScreenOption[];
};

export function getCurrentScreen(
    state: AppState,
    catalog: Map<SKU, MergedProduct>,
    locale: string,
): ScreenViewModel | null {
    const walk = state.walk;
    if (!walk || walk.confirmingContributionId) return null;
    const node = nodes[walk.currentNodeId];
    if (!node || node.type !== "pick") return null;

    const lastHistory = walk.history[walk.history.length - 1];
    const selected = new Set(lastHistory.selectedOptionIds);

    // Subcopy: an explicit node-level subcopy takes precedence; otherwise
    // fall back to the innermost sequence frame's subcopy override (e.g.
    // "1ST COLUMN" / "2ND COLUMN" passed by the column chain).
    const sequenceSubcopy = (() => {
        const topFrame = walk.sequenceStack[walk.sequenceStack.length - 1];
        if (!topFrame) return undefined;
        const seq = nodes[topFrame.chainNodeId];
        if (!seq || seq.type !== "sequence") return undefined;
        const entry = seq.chain[topFrame.index];
        return typeof entry === "string" ? undefined : entry.subcopy;
    })();

    const options = node.options.map((opt) =>
        buildScreenOption(
            opt,
            node.pick === "any" ? selected.has(getOptionId(opt)) : undefined,
            node.suppressPrices ?? false,
            catalog,
            locale,
        ),
    );

    // Hide "See More Details" where sibling tiles point at the same product: the
    // screen is then choosing a sub-attribute (e.g. Left/Right Hinge → one wine
    // column), so a per-tile product link is redundant. All tiles are kept; only
    // the shared link is cleared.
    const optionsWithUniqueDetailLinks = options.map((option) =>
        option.detailsSku &&
        options.filter((o) => o.detailsSku === option.detailsSku).length > 1
            ? { ...option, detailsSku: undefined }
            : option,
    );

    return {
        spoke: walk.spoke,
        entry: walk.entry,
        currentNodeId: walk.currentNodeId,
        prompt: node.prompt,
        subcopy: node.subcopy ?? sequenceSubcopy,
        pickMode: node.pick,
        options: optionsWithUniqueDetailLinks,
    };
}

/**
 * Non-accessory products reachable from a node, split by how the flow
 * commits them: `viaAddsSku` are fixed structurally when a screen is reached;
 * `viaOption` come from picking a tile — i.e. a product choice lies ahead.
 * Sequence nodes recurse into each chain entry.
 */
function reachableProducts(nodeId: string): {
    viaAddsSku: SKU[];
    viaOption: SKU[];
} {
    const visited = new Set<string>();
    const viaAddsSku = new Set<SKU>();
    const viaOption = new Set<SKU>();
    const visit = (id: string) => {
        if (visited.has(id)) return;
        visited.add(id);
        const node = nodes[id];
        if (!node) return;
        if (node.type === "sequence") {
            for (const entry of node.chain) {
                visit(typeof entry === "string" ? entry : entry.node);
            }
            return;
        }
        if (node.addsSku && !isAccessory(node.addsSku)) {
            viaAddsSku.add(node.addsSku);
        }
        for (const opt of node.options) {
            if (opt.sku && !isAccessory(opt.sku)) viaOption.add(opt.sku);
            if (opt.then) visit(opt.then);
        }
    };
    visit(nodeId);
    return { viaAddsSku: [...viaAddsSku], viaOption: [...viaOption] };
}

/**
 * All non-accessory SKUs reachable by walking forward from a node — the union
 * of `reachableProducts`'s two buckets. The pricing and image-cascade callers
 * only need the combined set, not the via-`addsSku`/via-option split. Used to
 * derive the "Starting at $X" price for grouping tiles.
 */
function reachableNonAccessorySkus(nodeId: string): SKU[] {
    const { viaAddsSku, viaOption } = reachableProducts(nodeId);
    return [...new Set([...viaAddsSku, ...viaOption])];
}

/**
 * The catalog is exhaustive by construction (`buildMergedCatalog` iterates
 * every key in `skuCatalog`), but the type system can't see that. This
 * helper turns a violation into a legible error instead of a downstream
 * `Cannot read property of undefined`.
 */
function catalogRow(catalog: Map<SKU, MergedProduct>, sku: SKU): MergedProduct {
    const row = catalog.get(sku);
    if (!row) throw new Error(`SKU ${sku} not found in product catalog`);
    return row;
}

/** Numeric price parsed from the row's `Money` shape, for math. */
function priceOf(catalog: Map<SKU, MergedProduct>, sku: SKU): number {
    return Number(catalogRow(catalog, sku).msrp.amount);
}

/**
 * Which Tile Images rule (see spec for details) fires for an option
 */
type ImageRule =
    | "static"
    | "sku"
    | "preferred-child"
    | "highest-priced"
    | "fallback";

type ImageSource = {
    rule: ImageRule;
    /** Source SKU. Undefined for `static` and `fallback`. */
    sku?: SKU;
};

/** Resolve the Tile Images cascade for one option. */
export function resolveImageSource(
    opt: Option,
    catalog: Map<SKU, MergedProduct>,
): ImageSource {
    if (opt.image) return { rule: "static" };
    if (opt.sku) return { rule: "sku", sku: opt.sku };
    if (!opt.then) return { rule: "fallback" };
    const reachable = reachableNonAccessorySkus(opt.then);
    if (reachable.length === 0) return { rule: "fallback" };
    // `preferredTileImage` is a build-time schema flag, not display data.
    const preferred = reachable.find((s) => skuCatalog[s].preferredTileImage);
    if (preferred) return { rule: "preferred-child", sku: preferred };
    const highest = reachable.reduce((max, s) =>
        priceOf(catalog, s) > priceOf(catalog, max) ? s : max,
    );
    return { rule: "highest-priced", sku: highest };
}

/** Map a raw `Option` plus a selection bit to its `ScreenOption` view-model. */
function buildScreenOption(
    opt: Option,
    isSelected: boolean | undefined,
    suppressPrices: boolean,
    catalog: Map<SKU, MergedProduct>,
    locale: string,
): ScreenOption {
    const optId = getOptionId(opt);
    const optIsAccessory = opt.sku ? isAccessory(opt.sku) : false;

    // Tracking the source Money alongside the amount lets us preserve
    // the catalog row's currency block when re-wrapping (see `withAmount`).
    let priceInfo: { amount: number; source: Money } | undefined;
    if (!suppressPrices) {
        if (opt.sku) {
            const row = catalogRow(catalog, opt.sku);
            priceInfo = { amount: Number(row.msrp.amount), source: row.msrp };
        } else if (opt.then) {
            const reachable = reachableNonAccessorySkus(opt.then);
            if (reachable.length > 0) {
                priceInfo = {
                    amount: Math.min(
                        ...reachable.map((s) => priceOf(catalog, s)),
                    ),
                    source: catalogRow(catalog, reachable[0]).msrp,
                };
            }
        }
    }

    // A routing tile only previews a product when one is structurally fixed
    // ahead (a lone `addsSku`, e.g. a column size). A reachable `option.sku`
    // means a model picker lies ahead — even with one option today — so the
    // preview would blink with catalog cardinality; suppress it.
    const detailsSku = (() => {
        const hasUrl = (sku: SKU) => Boolean(catalogRow(catalog, sku).url);
        if (opt.sku) {
            return !optIsAccessory && hasUrl(opt.sku) ? opt.sku : undefined;
        }
        if (!opt.then) return undefined;
        const { viaAddsSku, viaOption } = reachableProducts(opt.then);
        if (viaOption.length > 0 || viaAddsSku.length !== 1) return undefined;
        return hasUrl(viaAddsSku[0]) ? viaAddsSku[0] : undefined;
    })();

    return {
        id: optId,
        label:
            opt.label ?? (opt.sku ? catalogRow(catalog, opt.sku).name : optId),
        description: opt.description,
        image: resolveImage(opt, catalog),
        imageVariant: opt.imageVariant,
        sku: opt.sku,
        price: priceInfo
            ? withAmount(priceInfo.source, priceInfo.amount, locale)
            : undefined,
        isAccessory: optIsAccessory,
        isSelected,
        detailsSku,
    };
}

/** Run the cascade and pick the right image (descriptor or catalog block). */
function resolveImage(
    opt: Option,
    catalog: Map<SKU, MergedProduct>,
): ImageChooserBlock | EngineImage {
    if (opt.image) return opt.image;
    const src = resolveImageSource(opt, catalog);
    return src.sku ? catalogRow(catalog, src.sku).image : fallbackTileImage;
}

/**
 * Return a `ScreenOption` for one option of a pick node, without
 * running a walk. Useful for fixtures and stories that want a real
 * view-model derived from `nodes.ts` rather than a hand-rolled literal
 * (which would silently drift from the engine's mapping). `isSelected`
 * is left undefined; callers that want a selected pick:any variant
 * spread: `{ ...getOptionViewModel(...), isSelected: true }`.
 */
export function getOptionViewModel(
    nodeId: string,
    optionId: string,
    catalog: Map<SKU, MergedProduct>,
    locale: string,
): ScreenOption {
    const node = nodes[nodeId];
    if (!node) throw new Error(`Unknown node: ${nodeId}`);
    if (node.type !== "pick") {
        throw new Error(`Node ${nodeId} is not a pick node`);
    }
    const opt = node.options.find((o) => getOptionId(o) === optionId);
    if (!opt) {
        throw new Error(`Option ${optionId} not found in node ${nodeId}`);
    }
    return buildScreenOption(
        opt,
        undefined,
        node.suppressPrices ?? false,
        catalog,
        locale,
    );
}

type ConfirmationViewModel = {
    contribution: Contribution;
    /** Whether the auto-added joining kit attaches to this card. See `getJoiningKitTargets`. */
    hasJoiningKit: boolean;
};

export function getConfirmation(state: AppState): ConfirmationViewModel | null {
    const walk = state.walk;
    if (!walk?.confirmingContributionId) return null;
    const c = state.suite.contributions.find(
        (x) => x.id === walk.confirmingContributionId,
    );
    if (!c) return null;
    const kitTargets = getJoiningKitTargets(state.suite);
    return {
        contribution: c,
        hasJoiningKit: kitTargets.has(c.id),
    };
}

/**
 * Per-card attribution for the joining-kit line. Freezer cards stay
 * silent because the kit ships in the box; the anchor is the earliest
 * non-freezer column. See spec § Column Joining Kit Auto-Add.
 */
export function getJoiningKitTargets(suite: SuiteState): Set<string> {
    const isFreezer = (c: Contribution) =>
        skuCatalog[c.mainSku].tags?.includes("column_freezer") ?? false;
    const isColumn = (c: Contribution) =>
        skuCatalog[c.mainSku].tags?.some((t) => t.startsWith("column_")) ??
        false;
    const nonFreezerColumns = suite.contributions
        .filter((c) => isColumn(c) && !isFreezer(c))
        .sort((a, b) => a.committedAt - b.committedAt);
    return new Set(nonFreezerColumns.slice(1).map((c) => c.id));
}

// ---------- Progress bar ----------

/**
 * View model for the initial-walk progress bar — one item per spoke in
 * canonical order. Two orthogonal fields:
 *
 *   - `state`: which icon does this spoke show? Four values matching
 *     the design's off / on / completed / skipped indicators.
 *   - `current`: is the shopper presently on this spoke? Used for the
 *     "you are here" highlight and `aria-current="step"`. Independent
 *     of `state` so a spoke that's both walked-past AND currently
 *     hosting the shopper (e.g. on a terminal confirmation screen)
 *     can render as "completed checkmark + current highlight."
 *
 * Returns null on populated walks and on non-walk views; the design
 * only shows the progress bar during the initial canonical walk.
 *
 * State derivation (the icon):
 *   - in `walkedPast` AND has a contribution → "completed"
 *   - in `walkedPast` AND no contribution → "skipped"
 *   - is the shopper's current spoke → "active"
 *   - otherwise → "not-yet"
 *
 * The walkedPast check takes priority over the active check: when a
 * terminal commit flags the current spoke walked-past on its own
 * confirmation screen, the progress bar's icon shows the spoke as
 * completed (it has finished). The `current` field still reflects
 * "you are here," so the renderer can layer both treatments. Back
 * from confirmation reverts walkedPast (see `goBack`), which restores
 * the spoke to active.
 */
export type ProgressItemState = "active" | "completed" | "skipped" | "not-yet";

type ProgressBarItem = {
    spokeId: SpokeId;
    label: string;
    state: ProgressItemState;
    current: boolean;
};

export function getProgressBar(state: AppState): ProgressBarItem[] | null {
    if (state.view !== "walk") return null;
    const walk = state.walk;
    if (walk.entry !== "initial") return null;

    const spokeHasContribution = (id: SpokeId) =>
        state.suite.contributions.some((c) => c.spoke === id);

    return hub.spokes.map((s) => {
        let progressState: ProgressBarItem["state"];
        if (walk.walkedPast.includes(s.id)) {
            progressState = spokeHasContribution(s.id)
                ? "completed"
                : "skipped";
        } else if (s.id === walk.spoke) {
            progressState = "active";
        } else {
            progressState = "not-yet";
        }
        return {
            spokeId: s.id,
            label: s.label,
            state: progressState,
            current: s.id === walk.spoke,
        };
    });
}

// ---------- Derived values ----------

type JoiningKitInfo = { sku: SKU; count: number };

/**
 * Applies all auto-add rules to the suite and returns the derived SKU
 * counts. The return shape supports multiple rules so adding a new one
 * is a data-only change.
 */
export const deriveAutoAdds = (suite: SuiteState): JoiningKitInfo[] => {
    const counts: Record<StructuralTag, number> = {
        column_refrigerator: 0,
        column_freezer: 0,
        column_wine: 0,
    };
    for (const c of suite.contributions) {
        for (const tag of skuCatalog[c.mainSku].tags ?? []) {
            counts[tag] += 1;
        }
    }
    return autoAddRules.map((rule) => ({
        sku: rule.skuToAdd,
        count: rule.formula(counts),
    }));
};

type DiscountEvaluation = {
    qualified: boolean;
    qualifyingPackageId: string | null;
    /** ID of the matching rule, or `null` when the suite did not qualify. */
    ruleId: string | null;
};

/**
 * Walks discount rules and returns the first qualification result. A
 * rule qualifies when (a) the suite contains a qualifying package and
 * (b) at least one further SKU carries a qualifying or additional-
 * eligible role beyond the package-consumed SKUs.
 */
export const evaluateDiscount = (suite: SuiteState): DiscountEvaluation => {
    for (const rule of discountRules) {
        // Build a list of role-carrying SKU instances (duplicates allowed).
        type RoleInstance = { role: DiscountRole; contribId: string };
        const instances: RoleInstance[] = [];
        for (const c of suite.contributions) {
            const pushFor = (sku: SKU) => {
                for (const role of skuCatalog[sku].roles) {
                    instances.push({ role, contribId: c.id });
                }
            };
            pushFor(c.mainSku);
            c.accessorySkus.forEach(pushFor);
        }

        for (const pkg of rule.qualifyingPackages) {
            // Try to consume one instance per required role. If we can,
            // then verify there's at least one leftover instance with a
            // "plus additional" role.
            const remaining = [...instances];
            let ok = true;
            for (const req of pkg.requires) {
                let taken = 0;
                while (taken < req.n) {
                    const idx = remaining.findIndex(
                        (ri) => ri.role === req.role,
                    );
                    if (idx < 0) {
                        ok = false;
                        break;
                    }
                    remaining.splice(idx, 1);
                    taken += 1;
                }
                if (!ok) break;
            }
            if (!ok) continue;
            const hasExtra = remaining.some((ri) =>
                rule.plusAdditionalRoles.includes(ri.role),
            );
            if (!hasExtra) continue;
            return {
                qualified: true,
                qualifyingPackageId: pkg.id,
                ruleId: rule.id,
            };
        }
    }
    return {
        qualified: false,
        qualifyingPackageId: null,
        ruleId: null,
    };
};

/**
 * Pricing: subtotal, discount, total, with the 10% applied only to SKUs
 * whose roles fall within the rule's discount scope.
 */
type SuitePricing = {
    subtotal: Money;
    savings: Money;
    total: Money;
};

export const computePricing = (
    suite: SuiteState,
    catalog: Map<SKU, MergedProduct>,
    locale: string,
): SuitePricing => {
    const disc = evaluateDiscount(suite);
    // Look up the rule that actually matched rather than assuming index 0.
    // When the suite doesn't qualify, we still need a scope/rate for the
    // pricing pass; falling back to an empty scope and rate 0 means
    // unqualified suites get subtotal-only pricing with savings === 0.
    const matchedRule = discountRules.find((r) => r.id === disc.ruleId);
    const scope = matchedRule?.discountScopeRoles ?? [];
    const rate = matchedRule?.discountRate ?? 0;
    const autoAdds = deriveAutoAdds(suite);

    let subtotal = 0;
    let discountable = 0;

    const addSku = (sku: SKU, qty = 1) => {
        const price = priceOf(catalog, sku) * qty;
        subtotal += price;
        const roles = skuCatalog[sku].roles;
        if (roles.some((r) => scope.includes(r))) discountable += price;
    };

    for (const c of suite.contributions) {
        addSku(c.mainSku);
        c.accessorySkus.forEach((s) => addSku(s));
    }
    for (const aa of autoAdds) {
        if (aa.count > 0) addSku(aa.sku, aa.count);
    }

    const savings = disc.qualified ? discountable * rate : 0;
    // Use any catalog row's msrp as a Money template — every row shares
    // the same currency shape for our launch.
    const template = catalog.values().next().value!.msrp;
    return {
        subtotal: withAmount(template, subtotal, locale),
        savings: withAmount(template, savings, locale),
        total: withAmount(template, subtotal - savings, locale),
    };
};
